import { type FilterBase_str_ as RebalFilter } from '@autone/openapi-rebalancing';
import { type Filter as ReorderFilter } from '@autone/openapi-reorder';
import {
  type AllocationFilter_FilterField_,
  type AllocationFilterOption,
  type FilterField,
} from '@autone/openapi-replenishment';
import {
  type CommonTranslationFunction,
  type GeneralCommonPaths,
} from '@autone/translations';
import { type CustomerCommonPaths } from '@autone/translations';
import {
  capitalizeFirstLetter,
  type FilterDimensionObj,
  type FilterType,
} from '@autone/utils';

import { type AdvancedFilterOperators } from '../../../types';
import { type AdvancedFiltersStorage } from '../../../utils';
import { type AdvancedFiltersState } from '../advanced-filters';
import { getAdvancedFiltersSelectedOptionsCount } from '../advanced-filters/v1/AdvancedFilters.helpers';
import {
  type GeneralFilterGroup,
  type GeneralFilterOption,
  getGeneralFiltersSelectedOptionsCount,
  isSizeDimension,
  type SelectedGeneralFilters,
} from '../general-filters';

import {
  type AdvancedConditionLabels,
  type TotalFiltersCountPayload,
} from './types';

export const getConditions = (
  common: CommonTranslationFunction,
): {
  id: AdvancedConditionLabels;
  translatedDisplayName: string;
}[] =>
  [
    { id: 'eq', translatedDisplayName: common('advanced-filters.equal-to') },
    {
      id: 'gt',
      translatedDisplayName: common('advanced-filters.greater-than'),
    },
    { id: 'lt', translatedDisplayName: common('advanced-filters.lower-than') },
    {
      id: 'ge',
      translatedDisplayName: common('advanced-filters.greater-than-equal'),
    },
    {
      id: 'le',
      translatedDisplayName: common('advanced-filters.lower-than-equal'),
    },
  ] as const;

const filterGeneralTranslationsMap: Record<string, GeneralCommonPaths> = {
  region_id: 'filters.region',
  location_id: 'filters.location',
  season_id: 'filters.season',
  manufacture_id: 'filters.manufacturer',
  article_id: 'filters.articles',
  merchandising_flag: 'filters.merchandising-flag',
  location_type_id: 'filters.location-types',
  country_id: 'filters.country',
  material_id: 'filters.material',
  color_id: 'filters.color',
  brand_id: 'filters.brand',
  collection_type: 'filters.collection-type',
  product_id: 'filters.product',
  from_location_id: 'filters.from-location',
  from_country_id: 'filters.from-country',
  to_location_id: 'filters.to-location',
  to_country_id: 'filters.to-country',
  lifecycle: 'filters.lifecycle',
  class_id: 'filters.class',
  size_id: 'filters.size',
} as const;

const filterCustomerTranslationsMap: Record<string, CustomerCommonPaths> = {
  department_id: 'filters.$department',
  event_id: 'filters.$events',
  sub_department_id: 'filters.$sub-department',
  style_id: 'filters.$style',
  territory_id: 'filters.$territory',
  sub_class_id: 'filters.$sub-class',
  gender_id: 'filters.$gender',
} as const;

export const convertAllocationGeneralFiltersToGeneralFilterGroups = ({
  filters,
  common,
}: {
  filters?: (AllocationFilter_FilterField_ | ReorderFilter | RebalFilter)[];
  common: CommonTranslationFunction;
}): GeneralFilterGroup[] =>
  filters
    ?.filter(({ options }) => options.length)
    .map(({ dimension_id, options }) => {
      const isSizeGroup = isSizeDimension(dimension_id);

      const transformedOptions: GeneralFilterOption[] = [];
      options.forEach(({ id, title }) => {
        if (id && title) {
          transformedOptions.push({
            id,
            translatedDisplayName: isSizeGroup
              ? title
              : capitalizeFirstLetter(title),
          });
        }
      });

      return {
        id: dimension_id,
        translatedDisplayName: capitalizeFirstLetter(
          dimension_id in filterCustomerTranslationsMap
            ? common.withCustomer(filterCustomerTranslationsMap[dimension_id])
            : common(
                filterGeneralTranslationsMap[dimension_id] || dimension_id,
              ),
        ),
        options: transformedOptions,
      };
    }) || [];

export const convertAllocationGeneralFiltersToSelectedGeneralFilters = (
  filters: FilterDimensionObj<FilterType>[],
): SelectedGeneralFilters => {
  const selectedGeneralFilters: SelectedGeneralFilters = {};

  filters.forEach((filter) => {
    const { dimension_id, options } = filter;
    const selectedOptions: { [optionId: string]: boolean } = {};

    options.forEach((option) => {
      const { id } = option;
      if (id) {
        selectedOptions[id] = true;
      }
    });

    selectedGeneralFilters[dimension_id] = selectedOptions;
  });

  return selectedGeneralFilters;
};

export const convertSelectedGeneralFiltersToAllocationGeneralFilters = <
  T extends AllocationFilter_FilterField_ | ReorderFilter | RebalFilter,
>(
  filters: SelectedGeneralFilters,
  generalFilters?: T[],
): FilterDimensionObj<FilterType>[] => {
  const activeFilters: FilterDimensionObj<FilterType>[] = [];
  Object.keys(filters).forEach((groupId) => {
    const options: AllocationFilterOption[] = [];
    Object.entries(filters[groupId]).map(([optionId, active]) => {
      if (active) {
        options.push({
          id: optionId,
          title: generalFilters
            ?.find(({ dimension_id }) => dimension_id === groupId)
            ?.options.find(({ id }) => id === optionId)?.title,
        });
      }
    });
    if (options.length) {
      activeFilters.push({
        dimension_id: groupId as FilterField,
        options,
      });
    }
  });

  return activeFilters;
};

export const convertAdvancedFiltersStateToAdvancedFiltersStorage = (
  filters: AdvancedFiltersState | null,
): AdvancedFiltersStorage | null =>
  filters && filters.conditions.length
    ? {
        conditions: filters.conditions.map(
          ({ filterId, filterConditionId, value }) => ({
            field: filterId,
            operator: filterConditionId as AdvancedFilterOperators,
            value: value.toString(),
          }),
        ),
        mode: filters.conditionCombineMode,
      }
    : null;

export const getTotalActiveFiltersCount = ({
  selectedGeneralFilters,
  activeAdvancedFilters,
}: TotalFiltersCountPayload): number =>
  (selectedGeneralFilters
    ? getGeneralFiltersSelectedOptionsCount(selectedGeneralFilters)
    : 0) +
  (activeAdvancedFilters
    ? getAdvancedFiltersSelectedOptionsCount(activeAdvancedFilters)
    : 0);
